import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { Produto, Cliente, ItemCarrinho, FormaPagamento } from '../types';

const CART_STORAGE_KEY = '@daex:carrinho';

interface CarrinhoStorage {
  cliente: Cliente | null;
  itens: ItemCarrinho[];
  desconto: number;
  formaPagamento: FormaPagamento | null;
  savedAt: string;
}

interface VendaContextData {
  cliente: Cliente | null;
  itens: ItemCarrinho[];
  desconto: number;
  formaPagamento: FormaPagamento | null;
  subtotal: number;
  total: number;
  quantidadeItens: number;
  hasCarrinhoSalvo: boolean;
  setCliente: (cliente: Cliente | null) => void;
  adicionarItem: (produto: Produto, quantidade: number) => void;
  removerItem: (produtoId: string) => void;
  atualizarQuantidade: (produtoId: string, quantidade: number) => void;
  aplicarDesconto: (valor: number) => void;
  setFormaPagamento: (forma: FormaPagamento) => void;
  limparCarrinho: () => void;
  limparVenda: () => void;
}

interface VendaProviderProps {
  children: ReactNode;
}

const VendaContext = createContext<VendaContextData>({} as VendaContextData);

// Carregar carrinho do localStorage
function loadCarrinho(): CarrinhoStorage | null {
  try {
    const saved = localStorage.getItem(CART_STORAGE_KEY);
    if (saved) {
      const data = JSON.parse(saved) as CarrinhoStorage;
      // Verificar se o carrinho foi salvo nas últimas 24 horas
      const savedAt = new Date(data.savedAt);
      const now = new Date();
      const diffHours = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60);
      if (diffHours < 24) {
        return data;
      }
      // Carrinho expirado, remover
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Erro ao carregar carrinho:', error);
    localStorage.removeItem(CART_STORAGE_KEY);
  }
  return null;
}

// Salvar carrinho no localStorage
function saveCarrinho(data: Omit<CarrinhoStorage, 'savedAt'>) {
  try {
    const toSave: CarrinhoStorage = {
      ...data,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Erro ao salvar carrinho:', error);
  }
}

// Limpar carrinho do localStorage
function clearCarrinhoStorage() {
  localStorage.removeItem(CART_STORAGE_KEY);
}

export function VendaProvider({ children }: VendaProviderProps) {
  const savedCarrinho = loadCarrinho();

  const [cliente, setClienteState] = useState<Cliente | null>(savedCarrinho?.cliente || null);
  const [itens, setItens] = useState<ItemCarrinho[]>(savedCarrinho?.itens || []);
  const [desconto, setDesconto] = useState(savedCarrinho?.desconto || 0);
  const [formaPagamento, setFormaPagamentoState] = useState<FormaPagamento | null>(savedCarrinho?.formaPagamento || null);
  const [hasCarrinhoSalvo] = useState(!!savedCarrinho && savedCarrinho.itens.length > 0);

  // Salvar no localStorage quando o carrinho mudar
  useEffect(() => {
    if (itens.length > 0 || cliente || desconto > 0) {
      saveCarrinho({ cliente, itens, desconto, formaPagamento });
    }
  }, [cliente, itens, desconto, formaPagamento]);

  // Wrapper para setCliente que também salva no storage
  const setCliente = useCallback((newCliente: Cliente | null) => {
    setClienteState(newCliente);
  }, []);

  // Wrapper para setFormaPagamento
  const setFormaPagamento = useCallback((forma: FormaPagamento) => {
    setFormaPagamentoState(forma);
  }, []);

  // Calcular subtotal
  const subtotal = itens.reduce((acc, item) => acc + item.subtotal, 0);

  // Calcular total
  const total = Math.max(subtotal - desconto, 0);

  // Quantidade total de itens
  const quantidadeItens = itens.reduce((acc, item) => acc + item.quantidade, 0);

  // Adicionar item ao carrinho
  const adicionarItem = useCallback((produto: Produto, quantidade: number) => {
    setItens((prevItens) => {
      // Verificar se o produto ja esta no carrinho
      const itemExistente = prevItens.find((item) => item.produto.id === produto.id);

      if (itemExistente) {
        // Atualizar quantidade
        return prevItens.map((item) =>
          item.produto.id === produto.id
            ? {
                ...item,
                quantidade: item.quantidade + quantidade,
                subtotal: (item.quantidade + quantidade) * item.precoUnitario - item.descontoItem,
              }
            : item
        );
      }

      // Adicionar novo item
      const preco = Number(produto.precoVenda);
      const novoItem: ItemCarrinho = {
        produto,
        quantidade,
        precoUnitario: preco,
        descontoItem: 0,
        subtotal: quantidade * preco,
      };

      return [...prevItens, novoItem];
    });
  }, []);

  // Remover item do carrinho
  const removerItem = useCallback((produtoId: string) => {
    setItens((prevItens) => prevItens.filter((item) => item.produto.id !== produtoId));
  }, []);

  // Atualizar quantidade de um item
  const atualizarQuantidade = useCallback((produtoId: string, quantidade: number) => {
    if (quantidade <= 0) {
      removerItem(produtoId);
      return;
    }

    setItens((prevItens) =>
      prevItens.map((item) =>
        item.produto.id === produtoId
          ? {
              ...item,
              quantidade,
              subtotal: quantidade * item.precoUnitario - item.descontoItem,
            }
          : item
      )
    );
  }, [removerItem]);

  // Aplicar desconto
  const aplicarDesconto = useCallback((valor: number) => {
    setDesconto(Math.max(valor, 0));
  }, []);

  // Limpar carrinho
  const limparCarrinho = useCallback(() => {
    setItens([]);
    setDesconto(0);
    clearCarrinhoStorage();
  }, []);

  // Limpar toda a venda (incluindo cliente)
  const limparVenda = useCallback(() => {
    setClienteState(null);
    setItens([]);
    setDesconto(0);
    setFormaPagamentoState(null);
    clearCarrinhoStorage();
  }, []);

  return (
    <VendaContext.Provider
      value={{
        cliente,
        itens,
        desconto,
        formaPagamento,
        subtotal,
        total,
        quantidadeItens,
        hasCarrinhoSalvo,
        setCliente,
        adicionarItem,
        removerItem,
        atualizarQuantidade,
        aplicarDesconto,
        setFormaPagamento,
        limparCarrinho,
        limparVenda,
      }}
    >
      {children}
    </VendaContext.Provider>
  );
}

// Hook para usar o contexto
export function useVenda(): VendaContextData {
  const context = useContext(VendaContext);

  if (!context) {
    throw new Error('useVenda deve ser usado dentro de um VendaProvider');
  }

  return context;
}

export default VendaContext;
